import type { useAuthorizationServiceV1 } from "@rw1/api-services";
import { AuditReportResponse } from "../types";
import { isBlob, readCompanyKey } from "./csvAndDateUtils";

export function canRunExport(
  isCompanyAdmin: boolean,
  companyId: string | undefined,
  fromDate: Date | null,
  toDate: Date | null,
): boolean {
  return (
    isCompanyAdmin &&
    !!companyId &&
    !!fromDate &&
    !!toDate &&
    fromDate.getTime() <= toDate.getTime()
  );
}

export function computeUtcRange(
  fromDate: Date,
  toDate: Date,
): { startUtc: Date; endUtc: Date } {
  const startUtc = new Date(
    Date.UTC(
      fromDate.getUTCFullYear(),
      fromDate.getUTCMonth(),
      fromDate.getUTCDate(),
      0,
      0,
      0,
      0,
    ),
  );
  const endUtc = new Date(
    Date.UTC(
      toDate.getUTCFullYear(),
      toDate.getUTCMonth(),
      toDate.getUTCDate(),
      23,
      59,
      59,
      999,
    ),
  );

  return { startUtc, endUtc };
}

function hasDataBlob(x: unknown): x is { data: Blob; fileName?: string } {
  if (typeof x !== "object" || x === null) return false;
  const obj = x as { data?: unknown; fileName?: unknown };

  return obj.data instanceof Blob;
}

export async function getAuditBlob(
  client: ReturnType<typeof useAuthorizationServiceV1>,
  startUtc: Date,
  endUtc: Date,
  companyId: string,
): Promise<{ blob: Blob; name?: string } | null> {
  const resp = (await client.getAuditReport(
    companyId,
    startUtc,
    endUtc,
  )) as AuditReportResponse;
  if (isBlob(resp)) return { blob: resp };
  if (hasDataBlob(resp)) return { blob: resp.data, name: resp.fileName };

  return null;
}

export function makeDownloadName(
  company: unknown,
  companyId: string,
  fromDate: Date,
  toDate: Date,
  suggested?: string,
): string {
  const key = readCompanyKey(company);
  const trimmed = key?.trim();
  const companyKey = trimmed && trimmed.length > 0 ? trimmed : companyId;
  const startStr = fromDate.toISOString().slice(0, 10);
  const endStr = toDate.toISOString().slice(0, 10);

  return suggested ?? `audit-log_${companyKey}_${startStr}_${endStr}.csv`;
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
